import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { DestroyController } from '@celerofinancas/class-utils';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  CHECKING_ACCOUNT_DROPDOWN_TOKEN,
  CdsCheckingAccountDropdownComponent,
} from '@celerofinancas/common-domain';
import { CdsInputsModule } from '@celerofinancas/design-system';
import { CompanyState } from '@celerofinancas/state-management';
import { UiDirectivesModule } from '@celerofinancas/ui-directives';
import { DdaService } from '@celerofinancas/ui-http-services';
import { ModalRef } from '@celerofinancas/ui-modals';
import { Store } from '@ngxs/store';
import { finalize, takeUntil, tap } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { ShimmerComponent } from '../shimmer/shimmer.component';
import { environment } from '@environments/environment';

/**
 * Identificador do evento `postMessage` enviado ao host CAIXA para iniciar o
 * fluxo de autorização do DDA (configurar o buscador de boletos). O host escuta
 * e conduz o redirecionamento. Contrato confirmado com o time da CAIXA
 * (card 86e1qyrcg): payload `{ type: 'autorizar-dda' }`.
 */
export const DDA_AUTHORIZATION_EVENT = 'autorizar-dda';

/**
 * DDA modal status.
 */
type DdaModalStatus = 'form' | 'success' | 'error';

/**
 * DDA account option.
 */
export interface DdaAccountOption {
  pk: string;
  [key: string]: unknown;
}

/**
 * DDA account data.
 */
export interface DdaAccountData {
  company: string;
  checking_account: string;
  created_at: string;
  updated_at: string;
}

/**
 * DDA account response.
 */
export interface DdaAccountResponse {
  checking_account_pk?: string;
  [key: string]: unknown;
}

/**
 * DDA modal data.
 */
export interface DdaModalData {
  accounts: DdaAccountOption[];
  description?: string;
  errorTitle?: string;
  errorDescription?: string;
  successTitle?: string;
  successDescription?: string;
}

/**
 * DDA modal component.
 */
@Component({
  selector: 'caixa-webcomponent-dda-modal',
  templateUrl: './dda-modal.component.html',
  styleUrls: ['./dda-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    UiDirectivesModule,
    CdsInputsModule,
    ReactiveFormsModule,
    FormsModule,
    ShimmerComponent,
    CdsCheckingAccountDropdownComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
        provide: CHECKING_ACCOUNT_DROPDOWN_TOKEN,
        useValue: {
          displayName: false,
          humanizedAccountFormatter: (value: any) => {
            return `CC: ${value.bank_account}-${value.bank_account_digit} - Ag: ${value.bank_branch} - Nº ${value.bank_id} `;
          },
        },
      },
  ],
})
export class DdaModalComponent extends DestroyController implements OnInit {

  /**
   * Initial selected account pk.
   */
  public initialSelectedAccount = '';

  /**
   * Current modal reference.
   */
  public readonly modalRef = inject(ModalRef) as ModalRef<unknown, DdaModalData>;

  /**
   * Change detector reference.
   */
  private readonly cdr = inject(ChangeDetectorRef);

  /**
   * Form builder.
   */
  private readonly fb = inject(FormBuilder);

  /**
   * DDA service.
   */
  private readonly ddaService = inject(DdaService);

  /**
   * Store management.
   */
  private readonly store = inject(Store);

  /**
   * Form group for the modal.
   */
  public form: FormGroup;

  /**
   * Current content status.
   */
  public status: DdaModalStatus = 'form';

  /**
   * Loading state for save button.
   */
  public isSaving = false;

  /**
   * Loading state for account selection.
   */
  public loading$ = new BehaviorSubject<boolean>(false);

  /**
   * Accounts list.
   */
  public get checkingAccounts(): DdaAccountOption[] {
    return this.modalRef.data?.accounts ?? [];
  }

  /**
   * Selected account value (for compatibility).
   */
  public get selectedAccount(): string {
    const selectedAccount = this.form?.get('checking_account')?.value as DdaAccountOption | string;

    if (typeof selectedAccount === 'string') {
      return selectedAccount;
    }

    return selectedAccount?.pk ?? '';
  }

  /**
   * Indicates if current selection can be saved.
   */
  public get canSave(): boolean {
    return !!this.selectedAccount
      && this.selectedAccount !== this.initialSelectedAccount
      && !this.isSaving;
  }

  /**
   * Modal description text.
   */
  public get description(): string {
    return this.modalRef.data?.description
      ?? 'Para ter a visão completa do seu caixa, informe em qual banco devemos vincular e trazer seus boletos previstos. Recomendamos que escolha a conta que mais utiliza para seus boletos a pagar (DDA).';
  }

  /**
   * Error state title.
   */
  public get errorTitle(): string {
    return this.modalRef.data?.errorTitle ?? 'Não foi possível salvar sua alteração.';
  }

  /**
   * Error state description.
   */
  public get errorDescription(): string {
    return this.modalRef.data?.errorDescription
      ?? 'No momento, não foi possível atualizar a conta bancária dos seus boletos previstos. Tente novamente mais tarde.';
  }

  /**
   * Success state title.
   */
  public get successTitle(): string {
    return this.modalRef.data?.successTitle ?? 'Alteração salva com sucesso!';
  }

  /**
   * Success state description.
   */
  public get successDescription(): string {
    return this.modalRef.data?.successDescription
      ?? 'Sua conta bancária para boletos previstos foi atualizada. Os dados da nova conta estão sendo processados e as informações serão refletidas no seu Fluxo de Caixa em até 24 horas.';
  }

  /**
   * Component initialization.
   */
  public ngOnInit(): void {
    const companyPk = this.store.selectSnapshot(CompanyState.getActiveCompanyPk);

    this.form = this.fb.group({
      checking_account: [null],
    });

    if (!companyPk) {
      this.form.get('checking_account')?.setValue(null);
      this.cdr.markForCheck();
      return;
    }

    this.loadCurrentAccount(companyPk);
  }

  /**
   * Loads the current DDA account selection.
   */
  private loadCurrentAccount(companyPk: string): void {
    this.loading$.next(true);

    this.ddaService
      .getAccountDda(companyPk)
      .pipe(
        takeUntil(this.destroyController$),
        tap(() => this.cdr.markForCheck()),
        finalize(() => {
          this.loading$.next(false);
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response: Partial<HttpResponse<DdaAccountData>>) => {
          const currentAccountPk = response?.body?.checking_account;
          const activeAccountDda = this.checkingAccounts
            .find(({ pk }) => pk === currentAccountPk)
            ?? null;

          this.form.get('checking_account')?.setValue(activeAccountDda);
          this.initialSelectedAccount = activeAccountDda?.pk ?? '';
          this.cdr.markForCheck();
        },
        error: () => {
          this.form.get('checking_account')?.setValue(null);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Saves selected account and updates modal content state.
   */
  public save(): void {
    if (!this.canSave) {
      return;
    }

    const companyPk = this.store.selectSnapshot(CompanyState.getActiveCompanyPk);

    if (!companyPk) {
      this.status = 'error';
      this.cdr.markForCheck();
      return;
    }

    this.isSaving = true;
    const payloadDda = {
      company_pk: companyPk,
      checking_account_pk: this.selectedAccount,
    };

    this.ddaService
      .updateAccountDda(payloadDda)
      .pipe(
        takeUntil(this.destroyController$),
        finalize(() => {
          this.isSaving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.status = 'success';
          this.cdr.markForCheck();
        },
        error: () => {
          this.status = 'error';
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Closes current modal.
   */
  public close(): void {
    this.modalRef.close();
  }

  /**
   * Dispara o evento de autorização do DDA para o host CAIXA via `postMessage`.
   * Acionado pelo link "configurar o buscador de boletos" — o host (CAIXA)
   * escuta o evento `autorizar-dda` e conduz o redirecionamento do fluxo.
   * O `targetOrigin` vem do environment (origin da CAIXA em prod; `*` em dev).
   */
  public requestDdaAuthorization(): void {
    window.parent.postMessage(
      { type: DDA_AUTHORIZATION_EVENT },
      environment.ddaAuthorizationTargetOrigin,
    );
  }
}
